import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
  Chip,
} from '@mui/material';
import {
  ExitToApp as LogoutIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  MoreVert as MoreVertIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { useThemeMode } from '../contexts/ThemeModeContext';

const ChatHeader: React.FC = () => {
  const { user, logout } = useAuth();
  const { clearMessages, activeConversationId, conversations } = useChat();
  const { mode, toggleMode } = useThemeMode();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  
  const activeConversation = conversations[activeConversationId || ''];

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  const handleClearChat = () => {
    clearMessages();
    handleMenuClose();
  };

  return (
    <AppBar 
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(10, 10, 10, 0.8)' : 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.primary,
        zIndex: 1000
      }}
    >
      <Toolbar sx={{ 
        minHeight: 64,
        px: { xs: 2, md: 3 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2
      }}>
        {/* Left side - Context Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, overflow: 'hidden' }}>
          {activeConversation ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600, 
                color: theme.palette.text.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {activeConversation.title}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {activeConversation.messages.length} messages
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 32,
                height: 32,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)'
              }}>
                <img 
                  src="/atlasai-icon.svg" 
                  alt="AI" 
                  style={{ width: 20, height: 20, filter: 'brightness(0) invert(1)' }}
                />
              </Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 700, 
                fontSize: '1.1rem',
                background: theme.palette.mode === 'dark' 
                  ? 'linear-gradient(135deg, #ffffff 0%, #94a3b8 100%)' 
                  : 'linear-gradient(135deg, #0f172a 0%, #475569 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                CoreSupport AI
              </Typography>
              <Chip 
                label="Beta" 
                size="small" 
                sx={{ 
                  height: 20, 
                  fontSize: '0.65rem', 
                  fontWeight: 600,
                  bgcolor: theme.palette.primary.main + '15',
                  color: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}30`
                }} 
              />
            </Box>
          )}
        </Box>

        {/* Right side - Actions */}
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
            <IconButton 
              onClick={toggleMode} 
              size="small"
              sx={{ 
                color: theme.palette.text.secondary,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '10px',
                p: 1,
                '&:hover': { 
                  backgroundColor: theme.palette.action.hover,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main
                },
              }}
            >
              {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <IconButton
            onClick={handleMenuOpen}
            size="small"
            sx={{ 
              color: theme.palette.text.secondary,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '10px',
              p: 1,
              '&:hover': { 
                backgroundColor: theme.palette.action.hover,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main
              },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                borderRadius: 3,
                minWidth: 180,
                bgcolor: theme.palette.background.paper,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1.5,
                  borderRadius: 1,
                  mx: 1,
                  my: 0.5,
                  typography: 'body2',
                  fontWeight: 500,
                  '&:hover': {
                    bgcolor: theme.palette.action.hover,
                  },
                },
              },
            }}
          >
            {user && (
              <Box sx={{ px: 3, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {user.email?.split('@')[0]}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {user.email}
                </Typography>
              </Box>
            )}
            
            <MenuItem onClick={handleClearChat}>
              <SettingsIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
              Clear Context
            </MenuItem>
            
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
              Sign Out
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default ChatHeader;
